import { useCallback } from "react";

import { loadGroupKey } from "../utils/Cryptoutils";
import { encryptGroup } from "../utils/encrypt";
import { saveMessage } from "../db/fsDB";

/* =========================================================
   SYSTEM EVENT TYPES
========================================================= */
export const SYSTEM_EVENTS = {
  MEMBER_ADDED:     "member_added",
  MEMBER_REMOVED:   "member_removed",
  ADMIN_PROMOTED:   "admin_promoted",
  ADMIN_DEMOTED:    "admin_demoted",
  INVITE_GENERATED: "invite_generated",
  INVITE_REVOKED:   "invite_revoked",
  GROUP_RENAMED:    "group_renamed",
  MEMBER_JOINED:    "member_joined",
  MEMBER_LEFT:      "member_left",
};

/* =========================================================
   SYSTEM TEMPLATES
   These always use the real actor name.
   The calling code is responsible for substituting "You"
   in the local display text (see buildLocalText below).
========================================================= */
const templates = {
  [SYSTEM_EVENTS.MEMBER_ADDED]:
    ({ actorName, targetName }) =>
      `${actorName} added ${targetName}`,

  [SYSTEM_EVENTS.MEMBER_REMOVED]:
    ({ actorName, targetName }) =>
      `${actorName} removed ${targetName}`,

  [SYSTEM_EVENTS.ADMIN_PROMOTED]:
    ({ actorName, targetName }) =>
      `${actorName} made ${targetName} an admin`,

  [SYSTEM_EVENTS.ADMIN_DEMOTED]:
    ({ actorName, targetName }) =>
      `${actorName} removed admin rights from ${targetName}`,

  [SYSTEM_EVENTS.INVITE_GENERATED]:
    ({ actorName }) =>
      `${actorName} generated a new invite link`,

  [SYSTEM_EVENTS.INVITE_REVOKED]:
    ({ actorName }) =>
      `${actorName} revoked the invite link`,

  [SYSTEM_EVENTS.GROUP_RENAMED]:
    ({ actorName, oldName, newName }) =>
      `${actorName} changed group name from "${oldName}" to "${newName}"`,

  [SYSTEM_EVENTS.MEMBER_JOINED]:
    ({ targetName }) =>
      `${targetName} joined via invite link`,

  [SYSTEM_EVENTS.MEMBER_LEFT]:
    ({ targetName }) =>
      `${targetName} left the group`,
};

/* =========================================================
   buildLocalText
   Produces a "You …" version of the system message for the
   actor's own optimistic bubble, keeping the encrypted
   payload as the real name for all other members.

   Rules
   ─────
   • actorName appears at the start  → replace with "You"
   • actorName appears elsewhere     → replace with "you" (lowercase)
   • targetName === actorName        → leave as-is (edge case: self-action)
========================================================= */
function buildLocalText(plainText, actorName, myName) {
  // Only substitute when the current user is the actor
  if (!actorName || !myName) return plainText;
  if (actorName.trim().toLowerCase() !== myName.trim().toLowerCase()) return plainText;

  // Replace leading occurrence with "You" (capital), others with "you"
  let replaced = false;
  return plainText.replace(new RegExp(escapeRegExp(actorName), "gi"), (match, offset) => {
    if (!replaced && offset === 0) {
      replaced = true;
      return "You";
    }
    return "you";
  });
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/* =========================================================
   HOOK
========================================================= */
export function useSystemMessage({
  socket,
  myChatId,
  myName,       // ← NEW: display name of the current user, for local "You" substitution
  setMessages,
  setGroups,
  getGroupKey,
  uuid,
}) {

  const sendSystemMessage = useCallback(async ({
    groupId,
    type,

    actorName,    // real name of the actor — always sent in the encrypted payload
    targetName,

    actorChatId,
    targetChatId,

    oldName,
    newName,
  }) => {

    const formatter = templates[type];

    if (!formatter) {
      console.warn("Unknown system event:", type);
      return null;
    }

    // ── 1. Build plaintext with the REAL actor name ─────────────────────────
    //    This is what gets encrypted and sent to the server.
    //    Other members will decrypt and see e.g. "James made Francis an admin".
    const plainText = formatter({
      actorName,
      targetName,
      oldName,
      newName,
    });

    // ── 2. Build local display text with "You" substituted ──────────────────
    //    Only the actor's own device sees this version in the optimistic bubble.
    //    e.g. "You made Francis an admin"
    const localText = buildLocalText(plainText, actorName, myName);

    // ── 3. Encrypt the REAL plain text (not the "You" version) ─────────────
    const groupKey = await loadGroupKey(groupId);

    if (!groupKey) {
      console.warn("Missing group key");
      return null;
    }

    const encrypted = await encryptGroup(plainText, groupKey);

    if (!encrypted) return null;

    const msgId     = uuid();
    const timestamp = Date.now();
    const chatKey   = getGroupKey(groupId);

    // ── 4. Build the full message record ─────────────────────────────────────
    const msg = {
      id:        msgId,
      msgId,

      groupId,
      chatKey,

      senderChatId: myChatId,

      fromMe:  true,
      isGroup: true,

      messageType:     "system",
      system:          true,
      systemEventType: type,

      // ✅ Use the localised text ("You …") for the actor's own optimistic bubble.
      //    Other members receive the encrypted real-name version and decrypt it.
      text: localText,

      actorChatId:  actorChatId  || myChatId,
      targetChatId: targetChatId || null,

      encryptedText: {
        ciphertext:         encrypted.ciphertext,
        iv:                 encrypted.iv,
        ephemeralPublicKey: null,
      },

      encryptedMedia:        null,
      replyToMessageId:      null,
      replyPreviewEncrypted: null,

      seen:    false,
      seenAt:  null,
      isDeleted: false,
      isEdited:  false,

      pinnedBy:  null,
      pinnedAt:  null,
      expiresAt: null,

      timestamp,
    };

    // ── 5. Optimistic insert into chat window ────────────────────────────────
    setMessages((prev) => ({
      ...prev,
      [chatKey]: [...(prev[chatKey] || []), msg],
    }));

    // ── 6. Optimistic sidebar preview ────────────────────────────────────────
    //    Show the "You …" version in the actor's own sidebar too.
    if (typeof setGroups === "function") {
      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? {
                ...g,
                lastMessage:    localText,
                lastActivityAt: timestamp,
              }
            : g
        )
      );
    }

    // ── 7. Persist to IDB (strip runtime-only `text`) ────────────────────────
    const { text: _stripped, ...forIDB } = msg;
    await saveMessage(forIDB);

    // ── 8. Emit to server ─────────────────────────────────────────────────────
    //    The server broadcasts the encrypted real-name text to all members.
    socket.emit("group-send-system-message", {
      groupId,
      msgId,

      systemEventType: type,

      actorChatId:  actorChatId  || myChatId,
      targetChatId: targetChatId || null,

      encryptedText: {
        ciphertext:         encrypted.ciphertext,
        iv:                 encrypted.iv,
        ephemeralPublicKey: null,
      },
    });

    return msg;

  }, [
    socket,
    myChatId,
    myName,
    setMessages,
    setGroups,
    getGroupKey,
    uuid,
  ]);

  return {
    sendSystemMessage,
    SYSTEM_EVENTS,
  };
}